import { Component } from "react";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import ProviderAffiliationService from "@app/projects/providerAffiliationsService";
import { AffiliationType, EntityTypeEnum } from "@app/projects/enumerators";
import { orderBy } from "lodash";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { ServicingProviderDetail } from "@app/projects/results/servicing-provider/servicingProviderDetail";

interface ProviderAffiliationsAccordionProps {  
  providerAffiliationVersionId: number;  
  provider: ServicingProviderDetail;
  entityType?: EntityTypeEnum;
}

interface ProviderAffiliationsAccordionState {
  records: ProviderAffiliationsTableData[];
}

interface ProviderAffiliationsTableData {
  [wafflesKeyColumnName]: string;
  affiliationType: string;
  name: string;
  npi: string;
  type: string;
  affiliationStrength: string;
  primary: string;
  entitySize: string;
  npi2Affiliations: ProviderNpi2Affiliations[];
}

interface ProviderNpi2Affiliations {
  name: string;
  npi2: string;
  affiliationStrength: string;
  primaryContractEntity: string;
  primaryOrganization: string;
  facilityTypeId: string;
}

export default class ProviderAffiliationsAccordionResults extends Component<ProviderAffiliationsAccordionProps, ProviderAffiliationsAccordionState> {

  constructor(props: ProviderAffiliationsAccordionProps) {
    super(props);
    this.buildRecords(); 
  }
  public async componentDidUpdate(prevProps: ProviderAffiliationsAccordionProps) {
    if (prevProps.provider.npi !== this.props.provider.npi) {
      this.buildRecords();
    }
  }

  public render() {
    if (this.state === null) {
      return (<>No provider affiliation results found.</>);
    }
    const customSort = (data: ProviderAffiliationsTableData[], column: keyof ProviderAffiliationsTableData, direction: SortDirections) => {
        let sortedData: ProviderAffiliationsTableData[] = [];
            switch (column) {
              case "affiliationStrength":
                sortedData = orderBy(data, (d) => Number(d[column].slice(0,d.affiliationStrength.length - 1)), direction === SortDirections.Desc ? "desc" : "asc");
                break;
              case "name":
                sortedData = orderBy(data, (d) => d[column], direction === SortDirections.Desc ? "desc" : "asc");
                break;
              case "type":
                sortedData = orderBy(data, (d) => d[column], direction === SortDirections.Desc ? "desc" : "asc");
                break;
              case "entitySize":
                sortedData = orderBy(data, (d) => d[column], direction === SortDirections.Desc ? "desc" : "asc");
                break;
              default:
                sortedData = data;
                break;
            }
        return sortedData;
      }

      const expansionData = new Map<string, ProviderAffiliationsTableData[]>();
      const buildExpansionData = (rows: ProviderNpi2Affiliations[]): ProviderAffiliationsTableData[] => {
        if (rows !== undefined) {
          return rows.map((row) => {
            const newRow: ProviderAffiliationsTableData = {
              affiliationStrength: row.affiliationStrength,
              affiliationType: '',
              entitySize: '',
              name: row.name,
              npi: row.npi2,
              npi2Affiliations: [],
              primary: row.primaryOrganization,
              type: row.facilityTypeId.toLowerCase() !== 'unknown' ? row.facilityTypeId : 'Organization',
              [wafflesKeyColumnName]: row.npi2,
            };
            return newRow;
          })
        } else {
          return [];
        }
      }

    this.state.records.forEach((values, key) => { expansionData.set(key.toString(), buildExpansionData(values.npi2Affiliations as ProviderNpi2Affiliations[])); });

    if (this.props.entityType === EntityTypeEnum.Facility) {
      return <>
      <>
        <h6 className="mb-2">Contract Entity</h6>
        <Waffles<ProviderAffiliationsTableData>
          columns={this.columnConfig}
          data={this.state.records.filter((x) => x.affiliationType === AffiliationType.ProfessionalAffiliation)}
          testId="provider-affiliationprofessional-results"
          fullWidth={true}
          dataExpansions={expansionData}
        />
      </>
    </>
    }
    else if (this.props.entityType === EntityTypeEnum.Individual || this.props.entityType === EntityTypeEnum.Unknown) {
      return <>
        <>
          <h6 className="mb-2">Professional Affiliation (Contract Entity)</h6>
          <Waffles<ProviderAffiliationsTableData>
            columns={this.columnConfig}
            data={this.state.records.filter((x) => x.affiliationType === AffiliationType.ProfessionalAffiliation)}
            testId="provider-affiliationprofessional-results"
            fullWidth={true}
            dataExpansions={expansionData}
          />
          <h6 className="mt-4 mb-2">Institutional Affiliation (Admitting Privileges)</h6>
          <Waffles<ProviderAffiliationsTableData>
            columns={this.columnConfig}
            data={this.state.records.filter((x) => x.affiliationType === AffiliationType.InstitutionalAffiliation)}
            testId="provider-affiliationinstituional-results"
            fullWidth={true}
            customSort={customSort}
            dataExpansions={expansionData}
          />
        </>
      </>
    }
  }

  private buildRecords = () => {
    const providerAffiliationService = new ProviderAffiliationService();
    providerAffiliationService.GetLatestContractEntitiesByNpi1(this.props.provider.npi, false).then((data) => {
      if (data.length > 0) {
        const records = data.sort((a, b) => b.contractEntityAffiliationStrength - a.contractEntityAffiliationStrength).map((record, index) => ({
          affiliationStrength: `${Math.round(Number(record.contractEntityAffiliationStrength) * 100)}%`,
          affiliationType: record.contractEntityAffiliationType,
          entitySize: record.entitySize,
          key: index.toString(),
          name: record.contractEntityName,
          npi: '---',
          npi2Affiliations: record.npi2Affiliations !== undefined && record.npi2Affiliations.length > 0 ?
            record.npi2Affiliations.sort((a, b) => Number(b.affiliationStrength) - Number(a.affiliationStrength)).map((org) => ({
              affiliationStrength: `${Math.round(Number(org.affiliationStrength) * 100)}%`,
              facilityTypeId: '',
              name: org.name,
              npi2: org.npi2,
              primaryContractEntity: org.isPrimaryContractEntity === true ? 'Primary' : '',
              primaryOrganization: org.isPrimaryOrganization === true ? 'Primary' : '',
              type: ''
            })) as ProviderNpi2Affiliations[] :
            [{
              affiliationStrength: '---',
              facilityTypeId: '---',
              name: '---',
              npi2: '---',
              primaryContractEntity: '---',
              primaryOrganization: '---',
              type: '---'
            }],
          primary: record.contractEntityPrimary === true ? 'Primary' : '',
          type: record.contractEntitySubType,
        })) as ProviderAffiliationsTableData[];
        this.setState({
          records: records
        })
      } else {
        if (this.props?.provider?.totalClaimsVolumeDisplay !== 'Ghost') {
          providerAffiliationService.getContractEntityHeaderFieldsById(this.props.providerAffiliationVersionId, this.props.provider.professionalAffiliationId).then((ceInfo) => {
            const records: ProviderAffiliationsTableData[] = [];
            if (!!this.props?.provider?.professionalContractEntityName) {
              records.push({
                affiliationType: AffiliationType.ProfessionalAffiliation,
                entitySize: ceInfo.entitySize,
                key: this.props.provider.professionalContractEntityName,
                name: this.props.provider.professionalContractEntityName,
                type: ceInfo.entityType
              } as ProviderAffiliationsTableData);
            }

            this.setState({
              records: records
            })
          });
        }
      }
    });
  }

  private buildTooltipText = () => {
    return (<>
      <div className={"entity-type-tooltip-body text-start p-2"}>
        <div className={"header-row row border-bottom py-1"}>
          <div className={"col-6 label-s"}>
            Type
          </div>
          <div className={"col-6 label-s"}>
            Description
          </div>
        </div>
        <div className={"row my-2"}>
          <div className={"col label-tiny"}>
            Small Independent
          </div>
          <div className={"col"}>
            5 or fewer MD/DO Providers
          </div>
        </div>
        <div className={"row my-2"}>
          <div className={"col label-tiny"}>
            Medium Independent
          </div>
          <div className={"col"}>
            6-50 MD/DO Providers
          </div>
        </div>
        <div className={"row my-2"}>
          <div className={"col label-tiny"}>
            Large Independent
          </div>
          <div className={"col"}>
            Over 50 MD/DO Providers
          </div>
        </div>
        <div className={"row my-2"}>
          <div className={"col label-tiny"}>
            Non-IDN Health System
          </div>
          <div className={"col"}>
            Owns a single hospital (provider count can vary)
          </div>
        </div>
        <div className={"row my-2"}>
          <div className={"col label-tiny"}>
            IDN (Integrated Delivery Network)
          </div>
          <div className={"col"}>
            Owns two or more hospitals (provider count can vary)
          </div>
        </div>
      </div>
    </>);
  }

  private columnConfig: ColumnConfig = {
    /* oxlint-disable sort-keys */
    name: {
      displayText: "Name",
      testId: "county",
      sortOrder: 1,
      sizeContentToFit: true,      
    },
    type: {
      displayText: "Type",
      testId: "type",
      sortOrder: 2,
      maxWidth: "225px",
      sizeContentToFit: true,      
      displayElement:
        <>
          <TooltipWrapper containerClassName={"entity-type-tooltip-wrapper"} text={this.buildTooltipText()} position={"top"}>
            <QesIcon
              className={"me-2"}
              iconName={QesIcons.InfoCircle}
              testId="tooltip-entity-type"
              description={"Show tooltip"}
            />
            Type
          </TooltipWrapper>
        </>
    },
    npi: {
      displayText: "NPI",
      testId: "npi",
      sortOrder: 3,
      maxWidth: "150px",
      noSort: true,
      sizeContentToFit: true,
    },
    entitySize: {
      displayText: "Entity Size (MD/DO)",
      testId: "affiliation-entity-size",
      sortOrder: 4,
      maxWidth: "175px",
      sizeContentToFit: true,
    },
    affiliationStrength: {
      displayText: "Affiliation Strength",
      testId: "affiliation-strength",
      sortOrder: 5,
      maxWidth: "50px",
      sizeContentToFit: true,
    },
    primary: {
      displayText: "",
      testId: "primary",
      sortOrder: 6,
      noSort: true,
      maxWidth: "50px",
      sizeContentToFit: true,
    },
    /* oxlint-enable */
  }
}